<template>
  <div>
    <template v-if="!isEditing">
      <!-- Compact view - single line -->
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center flex-grow-1">
          <span class="small text-muted mr-2">{{ displayText || '-' }}</span>
          <span v-if="canEdit" class="badge badge-secondary badge-sm">{{ visibilityText }}</span>
        </div>
        <fa v-if="canEdit" icon="pencil-alt" class="text-muted cursor-pointer ml-2" @click="startEditing" />
      </div>
    </template>
    <template v-else>
      <!-- Edit mode with checkboxes -->
      <div class="d-flex flex-column">
        <div class="d-flex flex-wrap justify-content-center mb-2">
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`yearly_${productMasterId}_${salesLineId}`"
              :checked="localData && localData.user_date_yearly_input"
              @change="updateField('user_date_yearly_input', $event.target.checked)"
            />
            <label class="form-check-label small" :for="`yearly_${productMasterId}_${salesLineId}`">
              {{ $t('sandoz.yearly') }}
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`q1_${productMasterId}_${salesLineId}`"
              :checked="localData && localData.user_date_q1_input"
              @change="updateField('user_date_q1_input', $event.target.checked)"
            />
            <label class="form-check-label small" :for="`q1_${productMasterId}_${salesLineId}`">
              Q1
            </label>
          </div>
        </div>
        <div class="d-flex flex-wrap justify-content-center mb-2">
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`q2_${productMasterId}_${salesLineId}`"
              :checked="localData && localData.user_date_q2_input"
              @change="updateField('user_date_q2_input', $event.target.checked)"
            />
            <label class="form-check-label small" :for="`q2_${productMasterId}_${salesLineId}`">
              Q2
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`q3_${productMasterId}_${salesLineId}`"
              :checked="localData && localData.user_date_q3_input"
              @change="updateField('user_date_q3_input', $event.target.checked)"
            />
            <label class="form-check-label small" :for="`q3_${productMasterId}_${salesLineId}`">
              Q3
            </label>
          </div>
        </div>
        <div class="d-flex flex-wrap justify-content-center mb-2">
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`q4_${productMasterId}_${salesLineId}`"
              :checked="localData && localData.user_date_q4_input"
              @change="updateField('user_date_q4_input', $event.target.checked)"
            />
            <label class="form-check-label small" :for="`q4_${productMasterId}_${salesLineId}`">
              Q4
            </label>
          </div>
        </div>
        <div class="mt-1 mb-2">
          <b-form-select
            size="sm"
            :value="currentVisibility"
            @change="updateField('item_visibility', $event)"
            :options="visibilityOptions"
          />
        </div>
        <div class="text-center">
          <fa icon="check" class="text-success cursor-pointer" @click="isEditing = false" title="Done" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ProductMatrixCell',
  props: {
    productMasterId: {
      type: Number,
      required: true
    },
    salesLineId: {
      type: Number,
      required: true
    },
    salesLineData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      visibilityOptions: [
        { value: 'both', text: this.$t('sandoz.both') },
        { value: 'item', text: this.$t('sandoz.item') },
        { value: 'adhoc_item', text: this.$t('sandoz.adhoc_item') }
      ],
      localData: null,
      isEditing: false
    }
  },
  computed: {
    currentVisibility() {
      return this.localData ? this.localData.item_visibility : (this.salesLineData ? this.salesLineData.item_visibility : 'both')
    },
    visibilityText() {
      const option = this.visibilityOptions.find(opt => opt.value === this.currentVisibility)
      return option ? option.text : this.currentVisibility
    },
    displayText() {
      const parts = []
      const data = this.localData || this.salesLineData
      
      if (!data) {
        return null
      }
      
      if (data.user_date_yearly_input) {
        parts.push(this.$t('sandoz.yearly'))
      }
      if (data.user_date_q1_input) {
        parts.push('Q1')
      }
      if (data.user_date_q2_input) {
        parts.push('Q2')
      }
      if (data.user_date_q3_input) {
        parts.push('Q3')
      }
      if (data.user_date_q4_input) {
        parts.push('Q4')
      }
      
      return parts.length > 0 ? parts.join(', ') : null
    },
    canEdit() {
      // Only allow editing if salesLineData exists and has an id (product has a real database record)
      if (!this.salesLineData || this.salesLineData === null || this.salesLineData === undefined) {
        return false
      }
      // Check if it has an id property with a truthy value (indicating a real database record)
      return this.salesLineData.hasOwnProperty('id') && 
             this.salesLineData.id !== null && 
             this.salesLineData.id !== undefined
    }
  },
  watch: {
    salesLineData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.localData = { ...newVal }
        } else {
          this.localData = {
            id: null,
            user_date_yearly_input: false,
            user_date_q1_input: false,
            user_date_q2_input: false,
            user_date_q3_input: false,
            user_date_q4_input: false,
            item_visibility: 'both'
          }
        }
        // Reset edit mode when data changes (e.g., after save/reload)
        this.isEditing = false
      }
    }
  },
  methods: {
    startEditing() {
      // Only allow editing if salesLineData exists
      if (this.canEdit) {
        this.isEditing = true
      }
    },
    updateField(field, value) {
      if (!this.localData) {
        this.localData = {
          id: null,
          user_date_yearly_input: false,
          user_date_q1_input: false,
          user_date_q2_input: false,
          user_date_q3_input: false,
          user_date_q4_input: false,
          item_visibility: 'both'
        }
      }

      this.localData[field] = value

      // Emit update with all current data, including id if it exists
      const updateData = {
        product_master_id: this.productMasterId,
        sales_line_id: this.salesLineId,
        ...this.localData
      }
      
      // Only include id if it exists (not null/undefined)
      if (this.localData.id) {
        updateData.id = this.localData.id
      }

      this.$emit('update', updateData)
    }
  }
}
</script>

<style scoped>
.form-check {
  margin-bottom: 0.25rem;
}
.form-check-label {
  font-size: 0.75rem;
  margin-left: 0.25rem;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  opacity: 0.7;
}
.badge-sm {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
}
</style>
